import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The shop's terms and warranty statement on a document (M2-48): the public
 * estimate and invoice pages and the office's estimate and invoice pages
 * share this one block, so the four cannot drift.
 *
 * <b>Always the snapshot the document went out with</b> — the response's
 * `terms`, never `GET /account/terms`. A document sent before M2-48, or by a
 * shop with no terms, has none, and this renders nothing.
 *
 * Plain text, printed as written: `pre-wrap` keeps the shop's line breaks and
 * blank lines, and React escapes every character, so nothing in a box can
 * become markup.
 */
export function DocumentTerms({
  terms,
  warrantyStatement,
  termsHeading,
  footnote,
}: {
  terms: string | null | undefined;
  warrantyStatement: string | null | undefined;
  /** "Estimate terms" or "Invoice terms". */
  termsHeading: string;
  /** The office's line under the block (which saved version it is); the customer's page has none. */
  footnote?: ReactNode;
}) {
  const { t } = useTranslation();
  const id = useId();
  if (!terms && !warrantyStatement) return null;

  return (
    <section aria-labelledby={`${id}-heading`} className="space-y-4" data-testid="document-terms">
      <h2 id={`${id}-heading`} className="text-base font-semibold">
        {t('documentTerms.heading')}
      </h2>
      {terms ? (
        <div className="space-y-1">
          <h3 className="text-sm font-semibold">{termsHeading}</h3>
          <p className="text-sm break-words whitespace-pre-wrap" data-testid="document-terms-text">
            {terms}
          </p>
        </div>
      ) : null}
      {warrantyStatement ? (
        <div className="space-y-1">
          <h3 className="text-sm font-semibold">{t('documentTerms.warranty')}</h3>
          <p
            className="text-sm break-words whitespace-pre-wrap"
            data-testid="document-warranty-text"
          >
            {warrantyStatement}
          </p>
        </div>
      ) : null}
      {footnote ? (
        <p
          className="text-xs text-[var(--color-muted-foreground)]"
          data-testid="document-terms-version"
        >
          {footnote}
        </p>
      ) : null}
    </section>
  );
}
